import React, { useEffect, useMemo } from 'react';
import PatientPage from 'pages/rpm/patients/patient';
import useUser from 'utils/useUser';
import ModuleProvider from 'infrastructure/providers/module-provider';
import { useNavigate, useParams } from 'react-router-dom';
import { isValidGuid } from 'infrastructure/functions';
import {
  patientPageAccessCcmStatuses,
  patientPageAccessRpmStatuses,
} from 'infrastructure/consts';
import { showResult } from 'infrastructure/hooks/utils';
import { useTranslation } from 'react-i18next';
import { Routes } from 'router/routes';
import { useRpmPatientDetails } from 'infrastructure/hooks/rpm';
import BaseLoader from 'components/atoms/base-loader';
import { useMultiPatientCall } from 'infrastructure/providers/multi-patient-call-context';
import ReadOnlyMarquee from 'components/atoms/marquee/read-only-marquee';
import { usePatientSession } from 'infrastructure/providers/patient-session-provider';
import { endActiveContact } from 'services/connectService';
import { useAppDispatch, useAppSelector } from 'store';

import UnifiedPatientProfile from './index';

const PatientProfile: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isPatient, isPractice } = useUser();
  const { patientGuid } = useParams();
  const { sessionId } = usePatientSession();

  const {
    connect: { handleConnectPopoutOpen },
  } = useAppDispatch();
  const connectPopoutOpen = useAppSelector(
    (state) => state.connect.connectPopoutOpen,
  );

  useEffect(() => {
    return () => {
      if (connectPopoutOpen) {
        endActiveContact();
        handleConnectPopoutOpen(false);
      }
    };
  }, [connectPopoutOpen, patientGuid]);

  const {
    query: { patient, isLoading },
  } = useRpmPatientDetails({ patientGuid });

  const hasAccess = useMemo(() => {
    if (!patient) return false;
    const hasRpmAccess = patientPageAccessRpmStatuses.includes(
      patient.profile.status,
    );
    const hasCcmAccess = patientPageAccessCcmStatuses.includes(
      patient.profile.ccmStatus,
    );
    return hasRpmAccess || hasCcmAccess;
  }, [patient]);

  useEffect(() => {
    if (!patientGuid || !isValidGuid(patientGuid)) {
      showResult(t('errors.invalidGuid'));
      navigate(Routes.notFound, { replace: true });
    }
  }, [patientGuid, navigate]);

  useEffect(() => {
    if (patient && !hasAccess) {
      navigate(Routes.notFound, { replace: true });
    }
  }, [patient, hasAccess]);

  const { onPatientVisited } = useMultiPatientCall();

  useEffect(() => {
    if (isPatient || isPractice) return;
    if (patientGuid) onPatientVisited(patientGuid);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && patientGuid)
        onPatientVisited(patientGuid);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [patientGuid, onPatientVisited, isPatient, isPractice]);

  return (
    <BaseLoader loading={isLoading || !hasAccess}>
      <ReadOnlyMarquee />
      <ModuleProvider>
        {isPatient ? (
          <PatientPage />
        ) : (
          <UnifiedPatientProfile sessionId={sessionId} />
        )}
      </ModuleProvider>
    </BaseLoader>
  );
};

export default PatientProfile;
